import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Offcanvas,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  BsBoxArrowUpRight,
  BsCpu,
  BsDisplay,
  BsHddNetwork,
  BsPersonBadge,
  BsTag,
  BsCalendarEvent,
} from "react-icons/bs";

import InventarioService from "../../../Services/Catalogos/InventarioService";
import { DTOEquipoInventario } from "../../../DTO/Inventario/DTOEquipoInventario";
import { DTOMonitorInventario } from "../../../DTO/Inventario/DTOMonitorInventario";
import { DTOIPInventario } from "../../../DTO/Inventario/DTOIPInventario";
import {
  DTOEstadoEquipo,
  DTOSegmentoRed,
} from "../../../DTO/Inventario/DTOCatalogosInventario";
import { MsgInformativo } from "../../../Utils/Toast/msgInformativo";
import MonitoresEquipo from "./MonitoresEquipo";
import RedEquipo from "./RedEquipo";

interface DetalleEquipoProps {
  show: boolean;
  idEquipo: number | null;
  onCerrar: () => void;
}

interface MensajeInventario {
  encabezado: string;
  mensaje: string;
  color?: string;
}

const obtenerMensajeError = (error: unknown): string => {
  const errorAxios = error as {
    response?: { data?: { mensaje?: string } };
    message?: string;
  };

  return (
    errorAxios.response?.data?.mensaje ??
    errorAxios.message ??
    "Ocurrió un error inesperado."
  );
};

const DetalleEquipo = ({
  show,
  idEquipo,
  onCerrar,
}: DetalleEquipoProps) => {
  const [equipo, setEquipo] = useState<DTOEquipoInventario | null>(null);
  const [monitores, setMonitores] = useState<DTOMonitorInventario[]>([]);
  const [ips, setIPs] = useState<DTOIPInventario[]>([]);
  const [estadosEquipo, setEstadosEquipo] = useState<DTOEstadoEquipo[]>([]);
  const [segmentos, setSegmentos] = useState<DTOSegmentoRed[]>([]);

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [header, setHeader] = useState("");
  const [msgColor, setMsgColor] = useState("primary");

  const mostrarMensaje = ({
    encabezado,
    mensaje,
    color = "primary",
  }: MensajeInventario) => {
    setHeader(encabezado);
    setMsg(mensaje);
    setMsgColor(color);
    setShowMsg(true);
  };

  const cargarDetalle = async () => {
    if (idEquipo === null) return;

    setCargando(true);
    setError("");

    try {
      const [
        respuestaEquipo,
        respuestaMonitores,
        respuestaIPs,
        respuestaEstados,
        respuestaSegmentos,
      ] = await Promise.all([
        InventarioService.obtenerEquipoPorId(idEquipo),
        InventarioService.obtenerMonitoresPorEquipo(idEquipo),
        InventarioService.obtenerIPsPorEquipo(idEquipo),
        InventarioService.obtenerEstadosEquipo(),
        InventarioService.obtenerSegmentosRed(),
      ]);

      setEquipo(respuestaEquipo.data);
      setMonitores(respuestaMonitores.data ?? []);
      setIPs(respuestaIPs.data ?? []);
      setEstadosEquipo(respuestaEstados.data ?? []);
      setSegmentos(respuestaSegmentos.data ?? []);
    } catch (errorPeticion) {
      setError(obtenerMensajeError(errorPeticion));
    } finally {
      setCargando(false);
    }
  };

  const recargarRelacionados = async () => {
    if (idEquipo === null) return;

    const [respuestaMonitores, respuestaIPs, respuestaSegmentos] =
      await Promise.all([
      InventarioService.obtenerMonitoresPorEquipo(idEquipo),
      InventarioService.obtenerIPsPorEquipo(idEquipo),
      InventarioService.obtenerSegmentosRed(),
    ]);

    setMonitores(respuestaMonitores.data ?? []);
    setIPs(respuestaIPs.data ?? []);
    setSegmentos(respuestaSegmentos.data ?? []);
  };

  useEffect(() => {
    if (!show || idEquipo === null) return;
    void cargarDetalle();
  }, [show, idEquipo]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={onCerrar}
        placement="end"
        style={{ width: "min(900px, 98vw)" }}
      >
        <Offcanvas.Header closeButton className="border-bottom bg-white">
          <Offcanvas.Title className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 42,
                height: 42,
                color: "#0d6efd",
                backgroundColor: "#e8f1ff",
              }}
            >
              <BsCpu size={21} />
            </div>

            <div>
              <div className="fw-bold">Detalle del equipo</div>
              <div className="text-muted fw-normal" style={{ fontSize: 12 }}>
                Equipo, monitores, red y segmentos
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ backgroundColor: "#f6f8fb" }}>
          {cargando && (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted mt-2">Cargando información...</div>
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {!cargando && equipo && idEquipo !== null && (
            <>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3"
                        style={{
                          width: 55,
                          height: 55,
                          color: "#0d6efd",
                          backgroundColor: "#e8f1ff",
                        }}
                      >
                        <BsCpu size={27} />
                      </div>

                      <div>
                        <div className="text-muted small">Equipo seleccionado</div>
                        <h4 className="fw-bold mb-1">{equipo.nombreEquipo}</h4>
                        <Badge bg="primary">
                          {equipo.tipoEquipo ?? "Sin tipo"}
                        </Badge>
                      </div>
                    </div>

                    <Badge
                      bg={
                        equipo.estadoEquipo?.toLowerCase().includes("activo")
                          ? "success"
                          : "secondary"
                      }
                      className="px-3 py-2"
                    >
                      {equipo.estadoEquipo ?? "Sin estado"}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm mb-3">
                <Card.Header className="bg-white border-0 pt-3 fw-bold">
                  Información general
                </Card.Header>

                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <div
                        className="d-flex gap-3 rounded-3 p-3 h-100"
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #edf0f4",
                        }}
                      >
                        <BsTag size={19} className="text-primary mt-1" />
                        <div>
                          <div className="text-muted small">
                            Service Tag / Serial
                          </div>
                          <div className="fw-bold font-monospace">
                            {equipo.serviceTagSerial ?? "Sin registro"}
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
  <div
    className="d-flex gap-3 rounded-3 p-3 h-100"
    style={{
      backgroundColor: "#f8fafc",
      border: "1px solid #edf0f4",
    }}
  >
    <BsCalendarEvent
      size={19}
      className="text-primary mt-1"
    />

    <div>
      <div className="text-muted small">
        Fecha de mantenimiento
      </div>

      <div className="fw-bold">
        {equipo.fechaMantenimiento
          ? equipo.fechaMantenimiento
              .slice(0, 10)
              .split("-")
              .reverse()
              .join("/")
          : "Sin programar"}
      </div>
    </div>
  </div>
</Col>

                    <Col md={6}>
                      <div
                        className="d-flex gap-3 rounded-3 p-3 h-100"
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #edf0f4",
                        }}
                      >
                        <BsPersonBadge size={19} className="text-primary mt-1" />
                        <div>
                          <div className="text-muted small">Asignado a</div>
                          {equipo.numEmpleadoAsignado ? (
                            <>
                              <div className="fw-bold">{equipo.nombreEmpleado}</div>
                              <div className="text-muted small">
                                Empleado #{equipo.numEmpleadoAsignado}
                              </div>
                            </>
                          ) : equipo.nombreResponsable ? (
                            <>
                              <div className="fw-bold">
                                {equipo.nombreResponsable}
                              </div>
                              <div className="text-muted small">
                                Responsable externo
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="fw-bold text-muted">Sin asignar</div>
                              <div className="text-muted small">
                                Disponible en inventario
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div
                        className="d-flex gap-3 rounded-3 p-3 h-100"
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #edf0f4",
                        }}
                      >
                        <BsDisplay size={19} className="text-primary mt-1" />
                        <div>
                          <div className="text-muted small">
                            Monitores relacionados
                          </div>
                          <div className="fw-bold fs-5">{monitores.length}</div>
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div
                        className="d-flex gap-3 rounded-3 p-3 h-100"
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #edf0f4",
                        }}
                      >
                        <BsHddNetwork size={19} className="text-primary mt-1" />
                        <div>
                          <div className="text-muted small">Direcciones IP</div>
                          <div className="fw-bold fs-5">{ips.length}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {equipo.observaciones && (
                    <div
                      className="mt-3 rounded-3 p-3"
                      style={{
                        backgroundColor: "#fffbea",
                        border: "1px solid #f9e8a8",
                      }}
                    >
                      <div className="small fw-bold text-warning-emphasis">
                        Observaciones
                      </div>
                      <p className="mb-0 mt-1">{equipo.observaciones}</p>
                    </div>
                  )}

                  {equipo.ligaEspecificaciones && (
                    <Button
                      variant="outline-primary"
                      className="mt-3"
                      href={equipo.ligaEspecificaciones}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsBoxArrowUpRight className="me-2" />
                      Abrir especificaciones
                    </Button>
                  )}
                </Card.Body>
              </Card>

              <MonitoresEquipo
                idEquipo={idEquipo}
                equipo={equipo}
                monitores={monitores}
                estadosEquipo={estadosEquipo}
                onRecargar={recargarRelacionados}
                onMensaje={mostrarMensaje}
              />

              <RedEquipo
                idEquipo={idEquipo}
                ips={ips}
                segmentos={segmentos}
                onRecargar={recargarRelacionados}
                onMensaje={mostrarMensaje}
              />
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <MsgInformativo
        show={showMsg}
        header={header}
        msg={msg}
        msgColor={msgColor}
        closeToast={() => setShowMsg(false)}
      />
    </>
  );
};

export default DetalleEquipo;
